import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useParams} from "react-router";
import {Box, Paper, Typography} from "@mui/material";
import EnterpriseObject from "../eoec/eo/EnterpriseObject";
import enterpriseObjectClassFactory from "../eoec/EnterpriseObjectClassFactory";
import ChangeOrderMemberTableApplet from "../eoec/applet/impl/Foundation/ChangeOrderMemberTableApplet";
import ChangeOrderTasks from "../components/Foundation/ChangeOrderTasks";
import ApprovalTab from "../components/Approvals/ApprovalTab";
import PageHeader from "../components/Common/PageHeader";
import PersonaHeaderInfo from "../components/Common/PersonaHeaderInfo";
import CCButton from "../components/Common/CCButton";
import css_buttons from "../components/Common/css/CCButton.module.scss";
import CopyableValue from "../components/Common/CopyableValue";
import {statusPill} from "../util/statusPill";
import {hasPrivilege} from "../util/SessionContext";
import {CHANGE_ORDER} from "../util/changeOrderPrivileges";
import {formatLocal} from "../util/Formatters";
import {Grey5, Grey17} from "../theme/styles";
import AxiosFoundationAPI from "../util/AxiosFoundationAPI";
import {approvalMessage} from "../util/approvalMessages";

// NO `json: true` ON THESE CALLS. It is not an axios option -- axios decides by the
// payload -- and the untyped .js components get away with passing it where a .tsx does
// not: TS2769, "json does not exist in type AxiosRequestConfig". Copying a request out
// of an EC into a page is exactly how it arrives.

/**
 * One change order: what it is, what is in it, and what can be done to it next.
 *
 * THE ACTIONS ARE THE STATE MACHINE, and only the ones that are legal appear. Every one of them is
 * refused server-side in the wrong state, so showing all four always would be offering three doors
 * that answer 409 -- and a button that reliably fails teaches people the feature is broken.
 *
 *     DRAFT           Submit for approval, Void
 *     IN_REVIEW       Void          (signing happens in the approver's queue, not here)
 *     APPROVED        Close         (which is what releases every record it carries)
 *     IMPLEMENTATION  Close         (declines until the tasks are finished, and says so)
 *     CLOSED          nothing
 *     VOIDED          nothing
 *
 * CLOSE IS OFFERED IN IMPLEMENTATION even though it will not close anything yet, and that is
 * deliberate: the refusal names the reason -- outstanding tasks, or an effective date still ahead --
 * which is information the reader needs and cannot get by the button being absent. It is also how
 * somebody sets the effective date on a package that is otherwise ready.
 *
 * SUBMITTING GOES THROUGH THE ORDINARY APPROVAL ENDPOINT. A change order is approved by the same
 * engine as a record -- TargetType "ChangeOrder" -- so there is no change-order-shaped submit here.
 */

function Field({label, value}: {label: string; value: React.ReactNode}) {
    return (
        <div style={{display: "flex", flexDirection: "column", gap: 1, padding: "6px 0", minWidth: 0}}>
            <Typography sx={{fontSize: 12, color: Grey5, lineHeight: 1.4}}>{label}</Typography>
            <Typography component="div"
                        sx={{fontSize: 15, fontWeight: 400, color: Grey17, lineHeight: 1.45,
                             overflowWrap: "anywhere"}}>
                {value === null || value === undefined || value === "" ? "—" : value}
            </Typography>
        </div>
    );
}

export default function ChangeOrderDetail() {
    const {usid} = useParams();
    const [record, setRecord] = useState<any>(null);
    const [busy, setBusy] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    // A DECLINED CLOSE IS NOT AN ERROR. Separate state so it reads as a note in the page's own voice
    // rather than in red beside genuine refusals -- "the effective date has not arrived" is the
    // system working, and painting it as a failure would send somebody looking for a fault.
    const [note, setNote] = useState<string | null>(null);

    // THE MEMBER GRID HAS TO BE TOLD. It is a separate enterprise component with its own cache, so
    // reloading the change order leaves it showing whatever it fetched on mount -- and every action
    // on this page moves the MEMBERS as well as the package. Submitting drove SOP-00239 from
    // PENDING_CHANGE to IN_REVIEW and the grid went on displaying PENDING CHANGE until somebody
    // reloaded the browser, which for a controlled record is worse than a cosmetic staleness: the
    // screen asserts a lifecycle state that is no longer true.
    //
    // `invalidate()` is what the grid's own Refresh menu calls, rather than reaching into the query
    // internals from a page.
    const requeryMembers = useCallback(() => {
        membersEORef.current?.getEnterpriseComponent("ChangeOrderMember")?.invalidate?.();
    }, []);

    const load = useCallback(() => {
        if (!usid) return;
        AxiosFoundationAPI({method: "GET", url: "t1/v1/change_orders",
                            params: {USID: usid}})
            .then((response: any) => {
                const list = response?.data?.PlatformMessage?.ListOfChangeOrder?.ChangeOrder;
                setRecord(Array.isArray(list) ? list[0] : list);
            })
            .catch(() => setRecord(null))
            .finally(() => requeryMembers());
    }, [usid, requeryMembers]);

    useEffect(() => { load(); }, [load]);

    // Built once per change order. Rebuilding it on every render would hand the grid a fresh
    // component each time and it would re-query forever.
    const membersEORef = React.useRef<EnterpriseObject | undefined>(undefined);
    const membersEO: EnterpriseObject | undefined = useMemo(() => {
        const eo = enterpriseObjectClassFactory("ChangeOrderMember");
        // SCOPED DURING THE RENDER PASS, not in an effect: PlatformTable queries on mount, before
        // any effect here runs, so a scope set later would let the first load through unscoped and
        // briefly show every change order's members at once.
        eo?.getEnterpriseComponent("ChangeOrderMember")?.setScope?.("ChangeOrderUSID", usid);
        membersEORef.current = eo;
        return eo;
    }, [usid]);

    const act = useCallback((action: string, extra?: {[key: string]: unknown}) => {
        if (!usid) return;
        setBusy(true);
        setError(null);
        AxiosFoundationAPI({
            method: "PUT", url: "t1/v1/change_orders",
            data: {PlatformMessage: {"-idoc": "v1.ChangeOrder-001",
                                     ChangeOrder: {USID: usid, LifecycleAction: action, ...(extra || {})}}},
            headers: {"idoc-request": "v1.ChangeOrder-001", "idoc-response": "v1.ChangeOrder-001"},
        })
            .then((response: any) => {
                // A CLOSE THAT DID NOT CLOSE IS NOT AN ERROR, and must not be shown as one. When the
                // effective date is ahead or tasks are outstanding the server records the date,
                // leaves the state alone and says why -- so the page reports that as a note rather
                // than letting it look like nothing happened.
                const body = response?.data?.PlatformMessage?.ChangeOrder;
                if (body && body.Closed === false && body.Reason) setNote(body.Reason);
                return load();
            })
            // THE SERVER'S WORDS, NOT A GENERIC FAILURE. Every refusal here is a rule somebody needs
            // to understand -- "only an approved change order can be closed", "that record is already
            // in CO-00007" -- and replacing it with "something went wrong" throws that away.
            .catch((failure: any) => setError(approvalMessage(failure, "That did not work.")))
            .finally(() => setBusy(false));
    }, [usid, load]);

    const submit = useCallback(() => {
        if (!usid) return;
        setBusy(true);
        setError(null);
        AxiosFoundationAPI({
            method: "POST", url: "t1/v1/object_approval_requests",
            data: {TargetType: "ChangeOrder", RecordUSID: usid},
        })
            .then(() => load())
            .catch((failure: any) => setError(approvalMessage(failure, "That did not work.")))
            .finally(() => setBusy(false));
    }, [usid, load]);

    const state = record?.State;

    return (
        <Box sx={{display: "flex", flexDirection: "column", height: "100%", minWidth: 0}}>
            {/* THE ACTIVE PERSONA, as every other Foundation page shows it. This page was the one
                that did not, and on a record reached only THROUGH a persona that is the worst place
                to omit it: a change order shared with the Document Control Board is invisible to the
                same person's Position, so "which hat am I wearing" is the question the page most
                needs to answer. ObjectDetail, ObjectList and ChangeOrderList already do this. */}
            <PageHeader title="Change Orders">
                <PersonaHeaderInfo/>
            </PageHeader>

            <Box sx={{flex: 1, minHeight: 0, overflow: "auto", p: 2,
                      display: "flex", flexDirection: "column", gap: 2}}>
                <Paper elevation={0}
                       sx={{border: "1px solid #E4E7EC", borderRadius: "4px", p: 2,
                            boxSizing: "border-box"}}>
                    <Box sx={{display: "flex", alignItems: "center", gap: 2, mb: 1}}>
                        <Typography sx={{fontSize: 18, fontWeight: 600, color: Grey17}}>
                            {record?.Title || "Change order"}
                        </Typography>
                        {statusPill(state)}
                        <Box sx={{flex: 1}}/>
                        {/* TWO CONDITIONS ON EVERY ACTION, AND THEY ANSWER DIFFERENT QUESTIONS.
                            The state says whether the move is available at all; the privilege says
                            whether THIS caller may make it. Both are enforced server-side -- the
                            state by the write event, the privilege by
                            change_order_privilege_guard -- so neither of these is doing the
                            refusing, only declining to offer what would be refused. */}
                        {state === "DRAFT" && hasPrivilege(CHANGE_ORDER.SUBMIT) && (
                            <CCButton className={css_buttons.primary} disabled={busy}
                                      onClick={submit}>Submit for approval</CCButton>
                        )}
                        {(state === "APPROVED" || state === "IMPLEMENTATION") && hasPrivilege(CHANGE_ORDER.CLOSE) && (
                            <CCButton className={css_buttons.primary} disabled={busy}
                                      onClick={() => act("Close")}>Close and release</CCButton>
                        )}
                        {(state === "DRAFT" || state === "IN_REVIEW" || state === "IMPLEMENTATION")
                          && hasPrivilege(CHANGE_ORDER.VOID) && (
                            <CCButton className={css_buttons.secondary} disabled={busy}
                                      onClick={() => act("Void")}>Void</CCButton>
                        )}
                    </Box>

                    {error && (
                        <Typography sx={{fontSize: 14, color: "#D64535", mb: 1}}>{error}</Typography>
                    )}
                    {note && !error && (
                        <Typography sx={{fontSize: 14, color: Grey5, mb: 1}}>{note}</Typography>
                    )}

                    <Box sx={{display: "grid", gap: 2,
                              gridTemplateColumns: {xs: "1fr", md: "repeat(3, minmax(0, 1fr))"}}}>
                        <Field label="Change Order"
                               value={<CopyableValue text={record?.ChangeOrderID}/>}/>
                        <Field label="Type" value={record?.ObjectTypeName}/>
                        <Field label="Owner" value={record?.PrimaryPositionName}/>
                        <Field label="Raised" value={formatLocal(record?.CreatedDatetime, undefined)}/>
                        <Field label="Effective"
                               value={record?.EffectiveDatetime
                                   ? formatLocal(record.EffectiveDatetime, undefined)
                                   : "On close"}/>
                        <Field label="Closed"
                               value={record?.ClosedDatetime
                                   ? formatLocal(record.ClosedDatetime, undefined) : null}/>
                        <Field label="Description" value={record?.Description}/>
                        {/* ISO 13485 7.3.9 expects the reason and the impact assessment to be
                            recorded, not only the outcome -- so they are shown, not tucked away. */}
                        <Field label="Justification" value={record?.Justification}/>
                        <Field label="Impact assessment" value={record?.ImpactAssessment}/>
                    </Box>
                </Paper>

                <Paper elevation={0}
                       sx={{border: "1px solid #E4E7EC", borderRadius: "4px", p: 2, flex: 1,
                            minHeight: 320, boxSizing: "border-box"}}>
                    <ChangeOrderMemberTableApplet
                        enterpriseComponent={membersEO?.getEnterpriseComponent("ChangeOrderMember")}/>
                </Paper>

                {/* BELOW THE RECORDS, because the records are what the change order IS and the tasks
                    are what has to happen around them. It reloads the page on change: finishing the
                    last task moves the change order out of IMPLEMENTATION, so the header above would
                    otherwise keep claiming a state that is no longer true. */}
                <ChangeOrderTasks changeOrderUSID={usid} state={state} onChanged={load}/>

                {/* WHAT THIS PAGE WAS NOT SAYING. A change order reached through an approver's
                    persona is, for that person, a thing they have been asked to SIGN -- and the page
                    showed them the record, its members and its tasks without once mentioning the
                    request or offering the button. The answer to "what do I do now" was on another
                    screen they had no reason to look at.

                    The same component ObjectDetail uses, so a signature is given the same way
                    wherever it is asked for, and ALWAYS rendered rather than only when something is
                    outstanding: a section that vanishes once nothing is required answers "who has to
                    sign this?" with silence in the case where the answer -- nobody -- matters most.

                    onChanged reloads, because signing the last slot moves the change order's state
                    and the header above would otherwise keep claiming the old one. */}
                <Paper elevation={0}
                       sx={{border: "1px solid #E4E7EC", borderRadius: "4px", p: 2, mt: 2,
                            boxSizing: "border-box"}}>
                    <Typography sx={{fontSize: 16, fontWeight: 600, color: Grey17, mb: 1}}>
                        Approvals
                    </Typography>
                    <ApprovalTab targetType="ChangeOrder" recordUSID={usid!} recordStatus={state}
                                 onChanged={load}/>
                </Paper>
            </Box>
        </Box>
    );
}
